// --- React
import React from 'react';
// --- UI Components
import {DetailsModal} from '../../DetailsModal';
import {AttachmentsForm} from './attachments-form';
import Button from '@dtci/spellbook/Button';
import {Row} from 'antd';
// --- Redux
import {useSelector} from 'react-redux';
import {useCommentsActions} from '../../../store/comments/useComments';
// --- API
import {datastageApi} from '../../../api';
// --- Utilities
import {filterCriteria} from '../../../utils/filterCriteria';
// --- Hooks
import {useDeleteDialog} from '../../dialogs/dialogs/delete';
import {useLoading} from '../../../utils/hooks/useLoading';
import {useToastNotification} from '../../ToastNotifications';
import {usePermissions} from '../../../utils/permissions';

/**
 * AttachmentsModal Component
 *
 * This component is responsible for rendering the attachments modal in the comments section.
 * It allows users to view and manage attachments related to a specific comment.
 * It also provides functionality to delete the comment and its associated attachments.
 *
 * @returns {JSX.Element} The rendered AttachmentsModal component.
 */
export function AttachmentsModal() {
    const {openDeleteDialog} = useDeleteDialog();
    const {setLoadingTrue, setLoadingFalse} = useLoading();
    const {openToast} = useToastNotification();
    const {isAttachmentsModalOpen, comment, commentProduct, filterForm, commentRange, commentSort} = useSelector(
        (s) => s.comments,
    );
    const {updateComments} = useCommentsActions();
    const {isLegalAdmin, isProductCommentsAdmin, isLegalUser} = usePermissions();

    // Function to handle the upload dialog
    const onAttachmentsClose = () => {
        updateComments({
            isAttachmentsModalOpen: false,
            isEditModalOpen: false,
        });
    };

    // Function to handle the delete comment action
    const onOpenDeleteModal = () => {
        openDeleteDialog(
            'Yes, Delete',
            [
                `Are you sure you want to delete comment ${comment.title} (${comment.id})?`,
                'Please note: This will also delete all associated attachments. This action can not be reversed.',
            ],
            async () => {
                setLoadingTrue();
                try {
                    const res = await datastageApi.deleteComment(comment.id);

                    if (res?.success === true) {
                        const data = await datastageApi.comments(
                            filterCriteria(filterForm, commentProduct, 'comments'),
                            commentRange,
                            commentSort,
                        );
                        updateComments({
                            comments: data?.content,
                            totalPages: data?.totalPages,
                            totalItems: data?.totalElements,
                            currentPage: data?.pageable.pageNumber,
                            isAttachmentsModalOpen: false,
                            isEditModalOpen: false,
                        });
                    }
                    openToast('Comment deleted successfully', 'success');
                } catch (error) {
                    console.log('Error deleting comment:', error);
                    openToast('Error deleting comment', 'error');
                } finally {
                    setLoadingFalse();
                }
            },
        );
    };

    // Function to handle the save action of the comment
    const handleSaveComment = async () => {
        setLoadingTrue();
        try {
            if (!comment) return;

            const res = await datastageApi.saveComment(comment, commentProduct);

            if (res?.status === 200) {
                const commentList = await datastageApi.comments(
                    filterCriteria(filterForm, commentProduct, 'comments'),
                    commentRange,
                    commentSort,
                );

                updateComments({
                    comment: res?.data,
                    comments: commentList?.content,
                });
                openToast('Comment saved successfully', 'success');
            }
        } catch (error) {
            console.error('Error saving comment:', error);
            openToast('Error saving comment', 'error');
        } finally {
            setLoadingFalse();
        }
    };

    return (
        <DetailsModal
            isOpen={isAttachmentsModalOpen}
            onClose={onAttachmentsClose}
            modalHeader="Comment Attachments"
            footer={
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    {(isLegalAdmin || isProductCommentsAdmin) && (
                        <Button
                            iconType="fa-regular fa-trash"
                            sizeType="small"
                            styleType="link"
                            onClick={onOpenDeleteModal}
                        >
                            Delete
                        </Button>
                    )}
                    <div style={{ marginLeft: 'auto' }}>
                        {(isLegalAdmin || isProductCommentsAdmin || isLegalUser) && (
                            <Button
                                iconType="fa-regular fa-close"
                                sizeType="small"
                                styleType="link"
                                onClick={onAttachmentsClose}
                            >
                                Cancel
                            </Button>
                        )}
                        {(isLegalAdmin || isProductCommentsAdmin) && (
                            <Button
                                iconType="fa-regular fa-floppy-disk"
                                sizeType="small"
                                styleType="link"
                                onClick={handleSaveComment}
                            >
                                Save
                            </Button>
                        )}
                    </div>
                </div>
            }
        >
            <AttachmentsForm comment={comment} />
        </DetailsModal>
    );
}